/**
 * Account/org/IP enforcement plumbing (pure, testable).
 *
 * The API answers 403 `{ error, code, reason?, until? }` when an account is
 * suspended/banned, its organisation is suspended, or the caller's IP is
 * blocked. The axios interceptor forwards that to the auth store, which
 * stashes it in sessionStorage and hard-navigates to `/account-suspended`.
 */

export type EnforcementCode =
  | 'ACCOUNT_SUSPENDED'
  | 'ACCOUNT_BANNED'
  | 'ORG_SUSPENDED'
  | 'IP_BLOCKED';

export const ENFORCEMENT_CODES: ReadonlySet<string> = new Set<EnforcementCode>([
  'ACCOUNT_SUSPENDED',
  'ACCOUNT_BANNED',
  'ORG_SUSPENDED',
  'IP_BLOCKED',
]);

export function isEnforcementCode(value: unknown): value is EnforcementCode {
  return typeof value === 'string' && ENFORCEMENT_CODES.has(value);
}

export interface EnforcementInfo {
  code: EnforcementCode;
  reason: string | null;
  until: string | null;
}

export const ENFORCEMENT_STORAGE_KEY = 'mykol.enforcement';

export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function defaultStorage(): StorageLike | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage;
  } catch {
    return null;
  }
}

export function saveEnforcement(info: EnforcementInfo, storage: StorageLike | null = defaultStorage()): void {
  if (!storage) return;
  try {
    storage.setItem(
      ENFORCEMENT_STORAGE_KEY,
      JSON.stringify({ code: info.code, reason: info.reason ?? null, until: info.until ?? null, at: Date.now() })
    );
  } catch {
    /* quota / private mode */
  }
}

export function readEnforcement(storage: StorageLike | null = defaultStorage()): EnforcementInfo | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(ENFORCEMENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const obj = parsed as { code?: unknown; reason?: unknown; until?: unknown };
    if (!isEnforcementCode(obj.code)) return null;
    return {
      code: obj.code,
      reason: typeof obj.reason === 'string' && obj.reason.trim() ? obj.reason : null,
      until: typeof obj.until === 'string' && obj.until.trim() ? obj.until : null,
    };
  } catch {
    return null;
  }
}

export function clearEnforcement(storage: StorageLike | null = defaultStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(ENFORCEMENT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export interface EnforcementCopy {
  title: string;
  body: string;
}

export function enforcementCopy(code: string | null | undefined): EnforcementCopy {
  switch (code) {
    case 'ACCOUNT_SUSPENDED':
      return {
        title: 'Your account is suspended',
        body: 'Access to myKOL has been temporarily suspended by the platform team. You will not be able to sign in until it is reactivated.',
      };
    case 'ACCOUNT_BANNED':
      return {
        title: 'Your account has been banned',
        body: 'This account has been permanently disabled for violating the myKOL terms of service. If you believe this is a mistake, contact us using the button below.',
      };
    case 'ORG_SUSPENDED':
      return {
        title: 'Your organisation is suspended',
        body: 'The organisation this account belongs to has been suspended, so its members cannot use myKOL right now. Ask your organisation owner to contact us.',
      };
    case 'IP_BLOCKED':
      return {
        title: 'Access from this network is blocked',
        body: 'Requests from your current IP address have been blocked because of suspicious activity. Try again from another network, or contact us if you think this is an error.',
      };
    default:
      return {
        title: 'Access restricted',
        body: 'Your access to myKOL is currently restricted. Contact us if you need help getting back in.',
      };
  }
}
